import React, { useState, useEffect } from 'react';
import { 
    getCompanies, createCompany, updateCompany, deleteCompany,
    getClients, createClient, updateClient, deleteClient,
    getAllPlans, getClientSubscriptions, addClientSubscription, removeClientSubscription,
    getClientTransactions, approveTransaction
} from '../../services/adminService';
import { 
    Plus, Edit, Trash2, Search, Building2, MapPin, Phone, Globe, 
    Users, ChevronDown, ChevronRight, Mail, CreditCard, FileText,
    Check, X, ExternalLink, DollarSign, ShieldCheck, GraduationCap, User
} from 'lucide-react';
import { TransaccionStatus } from '../movimientos/TransaccionStatus';

const OrganizationManager = () => {
    const [companies, setCompanies] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCompanyId, setExpandedCompanyId] = useState(null);

    // Company Modal State
    const [editingCompany, setEditingCompany] = useState(null);
    const [companyFormData, setCompanyFormData] = useState({ 
        Nombre: '', Direccion: '', Ciudad: '', Pais: '', Telefono: '' 
    });
    const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);

    // Client Modal State
    const [editingClient, setEditingClient] = useState(null);
    const [clientFormData, setClientFormData] = useState({
        Nombre: '', Apellido: '', EMail: '', Telefono: '', CompaniaId: '',
        CampusHabilitado: false, FacturacionHabilitada: false, Activo: true
    });
    const [isClientModalOpen, setIsClientModalOpen] = useState(false);
    const [targetCompanyId, setTargetCompanyId] = useState(null); // For adding client to specific company

    // Subscriptions & Transactions Modal State (Simplified for brevity, can be expanded)
    const [selectedClientForSubs, setSelectedClientForSubs] = useState(null);
    const [clientSubscriptions, setClientSubscriptions] = useState([]);
    const [allPlans, setAllPlans] = useState([]);
    const [isSubsModalOpen, setIsSubsModalOpen] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState('');

    const [selectedClientForTxs, setSelectedClientForTxs] = useState(null);
    const [clientTransactions, setClientTransactions] = useState([]);
    const [isTxsModalOpen, setIsTxsModalOpen] = useState(false);


    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [companiesData, clientsData, plansData] = await Promise.all([
                getCompanies(),
                getClients(),
                getAllPlans().catch(() => [])
            ]);
            setCompanies(companiesData);
            setClients(clientsData);
            setAllPlans(plansData);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    // --- Company Handlers ---

    const handleCompanySubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCompany) {
                await updateCompany(editingCompany.CompaniaId, companyFormData);
            } else {
                await createCompany(companyFormData);
            }
            setIsCompanyModalOpen(false);
            setEditingCompany(null);
            setCompanyFormData({ Nombre: '', Direccion: '', Ciudad: '', Pais: '', Telefono: '' });
            loadData(); // Reload all to be safe
        } catch (error) {
            alert('Error al guardar compañía: ' + error.message);
        }
    };

    const openNewCompanyModal = () => {
        setEditingCompany(null);
        setCompanyFormData({ Nombre: '', Direccion: '', Ciudad: '', Pais: '', Telefono: '' });
        setIsCompanyModalOpen(true);
    };

    const handleEditCompany = (company, e) => {
        e.stopPropagation();
        setEditingCompany(company);
        setCompanyFormData({
            Nombre: company.Nombre,
            Direccion: company.Direccion || '',
            Ciudad: company.Ciudad || '',
            Pais: company.Pais || '',
            Telefono: company.Telefono || ''
        });
        setIsCompanyModalOpen(true);
    };

    const handleDeleteCompany = async (id, e) => {
        e.stopPropagation();
        if (window.confirm('¿Está seguro de eliminar esta compañía? Se eliminarán también sus clientes.')) {
            try {
                await deleteCompany(id);
                loadData();
            } catch (error) {
                alert('Error al eliminar: ' + error.message);
            }
        }
    };

    // --- Client Handlers ---

    const openNewClientModal = (companyId, e) => {
        e.stopPropagation();
        setTargetCompanyId(companyId);
        setEditingClient(null);
        setClientFormData({ 
            Nombre: '', Apellido: '', EMail: '', Telefono: '', CompaniaId: companyId,
            CampusHabilitado: false, FacturacionHabilitada: false, Activo: true
        });
        setIsClientModalOpen(true);
    };

    const handleEditClient = (client) => {
        setEditingClient(client);
        setClientFormData({
            Nombre: client.Nombre,
            Apellido: client.Apellido,
            EMail: client.EMail,
            Telefono: client.Telefono || '',
            CompaniaId: client.CompaniaId,
            CampusHabilitado: client.CampusHabilitado,
            FacturacionHabilitada: client.FacturacionHabilitada,
            Activo: client.Activo
        });
        setIsClientModalOpen(true);
    };

    const handleClientSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingClient) {
                await updateClient(editingClient.ClienteId, clientFormData);
            } else {
                await createClient(clientFormData);
            }
            setIsClientModalOpen(false);
            setEditingClient(null);
            loadData();
        } catch (error) {
            alert('Error al guardar cliente: ' + error.message);
        }
    };

    const handleDeleteClient = async (id) => {
        if (window.confirm('¿Está seguro de eliminar este cliente?')) {
            try {
                await deleteClient(id);
                loadData();
            } catch (error) {
                alert('Error al eliminar: ' + error.message);
            }
        }
    };

    // --- Subscription & Transaction Handlers (Reused logic) ---
    
    const handleManageSubscriptions = async (client) => {
        setSelectedClientForSubs(client);
        try {
            const subs = await getClientSubscriptions(client.ClienteId);
            setClientSubscriptions(subs);
            setIsSubsModalOpen(true);
        } catch (error) {
            alert('Error al cargar suscripciones: ' + error.message);
        }
    };

    const handleAddSubscription = async (e) => {
        e.preventDefault();
        if (!selectedPlanId) return;
        try {
            await addClientSubscription(selectedClientForSubs.ClienteId, selectedPlanId);
            const subs = await getClientSubscriptions(selectedClientForSubs.ClienteId);
            setClientSubscriptions(subs);
            setSelectedPlanId('');
        } catch (error) {
            alert('Error al agregar suscripción: ' + error.message);
        }
    };

    const handleRemoveSubscription = async (subId) => {
        if (window.confirm('¿Seguro que desea cancelar esta suscripción?')) {
            try {
                await removeClientSubscription(subId);
                const subs = await getClientSubscriptions(selectedClientForSubs.ClienteId);
                setClientSubscriptions(subs);
            } catch (error) {
                alert('Error al cancelar suscripción: ' + error.message);
            }
        }
    };

    const handleViewTransactions = async (client) => {
        setSelectedClientForTxs(client);
        try {
            const txs = await getClientTransactions(client.ClienteId);
            setClientTransactions(txs);
            setIsTxsModalOpen(true);
        } catch (error) {
            alert('Error al cargar transacciones: ' + error.message);
        }
    };

    const handleApproveTransaction = async (txId) => {
        if (window.confirm('¿Aprobar este pago y activar/renovar la suscripción?')) {
            try {
                await approveTransaction(txId);
                const txs = await getClientTransactions(selectedClientForTxs.ClienteId);
                setClientTransactions(txs);
                alert('Pago aprobado exitosamente');
            } catch (error) {
                alert('Error al aprobar: ' + error.message);
            }
        }
    };


    // --- Render Helpers ---

    const filteredCompanies = companies.filter(c => 
        c.Nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.Ciudad && c.Ciudad.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const getClientsForCompany = (companyId) => {
        return clients.filter(c => c.CompaniaId === companyId);
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Cargando organización...</div>;

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-2xl font-bold text-slate-800">Organización (Compañías y Clientes)</h2>
                <button
                    onClick={openNewCompanyModal}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium"
                >
                    <Plus size={20} /> Nueva Compañía
                </button>
            </div>

            {/* Search Bar */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                <input
                    type="text"
                    placeholder="Buscar compañías..."
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Companies List */}
            <div className="space-y-4">
                {filteredCompanies.map(company => {
                    const companyClients = getClientsForCompany(company.CompaniaId);
                    const isExpanded = expandedCompanyId === company.CompaniaId;

                    return (
                        <div key={company.CompaniaId} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden transition-all duration-200">
                            {/* Company Header Card */}
                            <div 
                                className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                                onClick={() => setExpandedCompanyId(isExpanded ? null : company.CompaniaId)}
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`p-2 rounded-lg ${isExpanded ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                                        <Building2 size={24} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-slate-800 text-lg">{company.Nombre}</h3>
                                        <div className="flex items-center gap-4 text-sm text-slate-500">
                                            {company.Ciudad && (
                                                <span className="flex items-center gap-1">
                                                    <MapPin size={14} /> {company.Ciudad}, {company.Pais}
                                                </span>
                                            )}
                                            <span className="flex items-center gap-1">
                                                <Users size={14} /> {companyClients.length} Cliente{companyClients.length !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-3">
                                    <button 
                                        onClick={(e) => handleEditCompany(company, e)}
                                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Editar Compañía"
                                    >
                                        <Edit size={18} />
                                    </button>
                                    <button 
                                        onClick={(e) => handleDeleteCompany(company.CompaniaId, e)}
                                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Eliminar Compañía"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                    <div className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                                        <ChevronDown size={20} className="text-slate-400" />
                                    </div>
                                </div>
                            </div>

                            {/* Expanded Content (Clients List) */}
                            {isExpanded && (
                                <div className="border-t border-slate-100 bg-slate-50/50 p-4">
                                    <div className="flex justify-between items-center mb-4 px-2">
                                        <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                                            <Users size={18} /> Clientes Asociados
                                        </h4>
                                        <button
                                            onClick={(e) => openNewClientModal(company.CompaniaId, e)}
                                            className="text-sm bg-white border border-slate-200 text-slate-600 px-3 py-1.5 rounded-lg hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors flex items-center gap-2 shadow-sm"
                                        >
                                            <Plus size={16} /> Agregar Cliente
                                        </button>
                                    </div>

                                    {companyClients.length === 0 ? (
                                        <div className="text-center py-8 text-slate-400 bg-white rounded-lg border border-dashed border-slate-200">
                                            No hay clientes registrados en esta compañía.
                                        </div>
                                    ) : (
                                        <div className="grid gap-3">
                                            {companyClients.map(client => (
                                                <div key={client.ClienteId} className="bg-white p-4 rounded-lg border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:shadow-md transition-shadow">
                                                    <div className="flex items-start gap-3">
                                                        <div className={`mt-2 w-2 h-2 rounded-full ${client.Activo ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                                        <div>
                                                            <div className="font-medium text-slate-800">
                                                                {client.Nombre} {client.Apellido}
                                                            </div>
                                                            <div className="text-sm text-slate-500 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                                                                <span className="flex items-center gap-1"><Mail size={12} /> {client.EMail}</span>
                                                                {client.Telefono && <span className="flex items-center gap-1"><Phone size={12} /> {client.Telefono}</span>}
                                                            </div>
                                                            <div className="mt-2 flex gap-2">
                                                                {client.CampusHabilitado && (
                                                                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">Campus</span>
                                                                )}
                                                                {client.FacturacionHabilitada && (
                                                                    <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">Suscripción</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2 self-end sm:self-center">
                                                        <button 
                                                            onClick={() => handleManageSubscriptions(client)}
                                                            className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                                                            title="Suscripciones"
                                                        >
                                                            <CreditCard size={18} />
                                                        </button>
                                                        <button 
                                                            onClick={() => handleViewTransactions(client)}
                                                            className="p-1.5 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                                            title="Transacciones"
                                                        >
                                                            <DollarSign size={18} />
                                                        </button>
                                                        <button 
                                                            onClick={() => handleEditClient(client)}
                                                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                            title="Editar"
                                                        >
                                                            <Edit size={18} />
                                                        </button>
                                                        <button 
                                                            onClick={() => handleDeleteClient(client.ClienteId)}
                                                            className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                            title="Eliminar"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* --- Modals --- */}

            {/* Company Modal */}
            {isCompanyModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-slate-800">
                                {editingCompany ? 'Editar Compañía' : 'Nueva Compañía'}
                            </h3>
                            <button onClick={() => setIsCompanyModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleCompanySubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={companyFormData.Nombre}
                                    onChange={e => setCompanyFormData({...companyFormData, Nombre: e.target.value})}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Ciudad</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={companyFormData.Ciudad}
                                        onChange={e => setCompanyFormData({...companyFormData, Ciudad: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">País</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={companyFormData.Pais}
                                        onChange={e => setCompanyFormData({...companyFormData, Pais: e.target.value})}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Dirección</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={companyFormData.Direccion}
                                    onChange={e => setCompanyFormData({...companyFormData, Direccion: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={companyFormData.Telefono}
                                    onChange={e => setCompanyFormData({...companyFormData, Telefono: e.target.value})}
                                />
                            </div>
                            <div className="pt-4 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsCompanyModalOpen(false)}
                                    className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Client Modal */}
            {isClientModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-slate-800">
                                {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
                            </h3>
                            <button onClick={() => setIsClientModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleClientSubmit} className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        value={clientFormData.Nombre}
                                        onChange={e => setClientFormData({...clientFormData, Nombre: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Apellido</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        value={clientFormData.Apellido}
                                        onChange={e => setClientFormData({...clientFormData, Apellido: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        required
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        value={clientFormData.EMail}
                                        onChange={e => setClientFormData({...clientFormData, EMail: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        value={clientFormData.Telefono}
                                        onChange={e => setClientFormData({...clientFormData, Telefono: e.target.value})}
                                    />
                                </div>
                                
                                {/* Company Selection - Disabled if pre-selected via context */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Compañía</label>
                                    <select
                                        required
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        value={clientFormData.CompaniaId}
                                        onChange={e => setClientFormData({...clientFormData, CompaniaId: e.target.value})}
                                        disabled={!!targetCompanyId && !editingClient} // Lock if adding to specific company
                                    >
                                        <option value="">Seleccione una compañía</option>
                                        {companies.map(c => (
                                            <option key={c.CompaniaId} value={c.CompaniaId}>{c.Nombre}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="md:col-span-2 space-y-4">
                                    <h4 className="font-medium text-slate-700 flex items-center gap-2">
                                        <ShieldCheck size={18} className="text-blue-600" />
                                        Permisos y Acceso
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        {/* Active Status Toggle */}
                                        <div 
                                            onClick={() => {
                                                const newActiveState = !clientFormData.Activo;
                                                setClientFormData({
                                                    ...clientFormData, 
                                                    Activo: newActiveState,
                                                    // If deactivating, disable other permissions
                                                    CampusHabilitado: newActiveState ? clientFormData.CampusHabilitado : false,
                                                    FacturacionHabilitada: newActiveState ? clientFormData.FacturacionHabilitada : false
                                                });
                                            }}
                                            className={`cursor-pointer p-4 rounded-xl border transition-all duration-200 flex flex-col gap-3 ${
                                                clientFormData.Activo 
                                                    ? 'bg-slate-50 border-slate-300 shadow-sm' 
                                                    : 'bg-white border-slate-200 hover:border-slate-300 opacity-60'
                                            }`}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className={`p-2 rounded-lg ${clientFormData.Activo ? 'bg-slate-200 text-slate-700' : 'bg-slate-100 text-slate-400'}`}>
                                                    <User size={20} />
                                                </div>
                                                <div className={`w-10 h-6 rounded-full p-1 transition-colors duration-200 ${clientFormData.Activo ? 'bg-slate-800' : 'bg-slate-200'}`}>
                                                    <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform duration-200 ${clientFormData.Activo ? 'translate-x-4' : 'translate-x-0'}`} />
                                                </div>
                                            </div>
                                            <div>
                                                <span className={`font-semibold block ${clientFormData.Activo ? 'text-slate-900' : 'text-slate-500'}`}>Usuario Activo</span>
                                                <span className="text-xs text-slate-500">Permitir inicio de sesión</span>
                                            </div>
                                        </div>

                                        {/* Campus Toggle */}
                                        <div 
                                            onClick={() => clientFormData.Activo && setClientFormData({...clientFormData, CampusHabilitado: !clientFormData.CampusHabilitado})}
                                            className={`cursor-pointer p-4 rounded-xl border transition-all duration-200 flex flex-col gap-3 ${
                                                !clientFormData.Activo 
                                                    ? 'bg-slate-50 border-slate-200 opacity-50 cursor-not-allowed'
                                                    : clientFormData.CampusHabilitado 
                                                        ? 'bg-blue-50 border-blue-200 shadow-sm' 
                                                        : 'bg-white border-slate-200 hover:border-slate-300'
                                            }`}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className={`p-2 rounded-lg ${clientFormData.CampusHabilitado ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>
                                                    <GraduationCap size={20} />
                                                </div>
                                                <div className={`w-10 h-6 rounded-full p-1 transition-colors duration-200 ${clientFormData.CampusHabilitado ? 'bg-blue-600' : 'bg-slate-200'}`}>
                                                    <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform duration-200 ${clientFormData.CampusHabilitado ? 'translate-x-4' : 'translate-x-0'}`} />
                                                </div>
                                            </div>
                                            <div>
                                                <span className={`font-semibold block ${clientFormData.CampusHabilitado ? 'text-blue-900' : 'text-slate-600'}`}>Campus</span>
                                                <span className="text-xs text-slate-500">Acceso a cursos y contenido</span>
                                            </div>
                                        </div>

                                        {/* Subscription Toggle */}
                                        <div 
                                            onClick={() => clientFormData.Activo && setClientFormData({...clientFormData, FacturacionHabilitada: !clientFormData.FacturacionHabilitada})}
                                            className={`cursor-pointer p-4 rounded-xl border transition-all duration-200 flex flex-col gap-3 ${
                                                !clientFormData.Activo 
                                                    ? 'bg-slate-50 border-slate-200 opacity-50 cursor-not-allowed'
                                                    : clientFormData.FacturacionHabilitada 
                                                        ? 'bg-emerald-50 border-emerald-200 shadow-sm' 
                                                        : 'bg-white border-slate-200 hover:border-slate-300'
                                            }`}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className={`p-2 rounded-lg ${clientFormData.FacturacionHabilitada ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                                                    <CreditCard size={20} />
                                                </div>
                                                <div className={`w-10 h-6 rounded-full p-1 transition-colors duration-200 ${clientFormData.FacturacionHabilitada ? 'bg-emerald-600' : 'bg-slate-200'}`}>
                                                    <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform duration-200 ${clientFormData.FacturacionHabilitada ? 'translate-x-4' : 'translate-x-0'}`} />
                                                </div>
                                            </div>
                                            <div>
                                                <span className={`font-semibold block ${clientFormData.FacturacionHabilitada ? 'text-emerald-900' : 'text-slate-600'}`}>Suscripción</span>
                                                <span className="text-xs text-slate-500">Gestión de planes y pagos</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsClientModalOpen(false)}
                                    className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Subscriptions Modal */}
            {isSubsModalOpen && selectedClientForSubs && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <div>
                                <h3 className="text-xl font-bold text-slate-800">Suscripciones</h3>
                                <p className="text-sm text-slate-500">{selectedClientForSubs.Nombre} {selectedClientForSubs.Apellido}</p>
                            </div>
                            <button onClick={() => setIsSubsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6 space-y-6">
                            {/* Add New Subscription */}
                            <form onSubmit={handleAddSubscription} className="flex gap-3 items-end bg-slate-50 p-4 rounded-lg">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Agregar Plan</label>
                                    <select
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                                        value={selectedPlanId}
                                        onChange={e => setSelectedPlanId(e.target.value)}
                                    >
                                        <option value="">Seleccionar plan...</option>
                                        {allPlans.map(plan => (
                                            <option key={plan.PlanId} value={plan.PlanId}>
                                                {plan.Producto} - {plan.Plan} (${plan.Precio})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <button 
                                    type="submit"
                                    disabled={!selectedPlanId}
                                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Agregar
                                </button>
                            </form>

                            {/* List Subscriptions */}
                            <div className="space-y-3">
                                <h4 className="font-medium text-slate-700">Suscripciones Activas</h4>
                                {clientSubscriptions.length === 0 ? (
                                    <p className="text-slate-500 text-sm italic">No hay suscripciones activas.</p>
                                ) : (
                                    clientSubscriptions.map(sub => (
                                        <div key={sub.SuscripcionId} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                                            <div>
                                                <div className="font-medium text-slate-800">{sub.Producto} - {sub.Plan}</div>
                                                <div className="text-xs text-slate-500">
                                                    Desde: {new Date(sub.FechaInicio).toLocaleDateString()} | 
                                                    Créditos: {sub.CreditosUsados}/{sub.CreditosTotales}
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => handleRemoveSubscription(sub.SuscripcionId)}
                                                className="text-red-500 hover:text-red-700 text-sm font-medium"
                                            >
                                                Cancelar
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Transactions Modal */}
            {isTxsModalOpen && selectedClientForTxs && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl overflow-hidden max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <div>
                                <h3 className="text-xl font-bold text-slate-800">Historial de Transacciones</h3>
                                <p className="text-sm text-slate-500">{selectedClientForTxs.Nombre} {selectedClientForTxs.Apellido}</p>
                            </div>
                            <button onClick={() => setIsTxsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="text-slate-500 text-sm border-b border-slate-200">
                                            <th className="p-3 font-medium">Fecha</th>
                                            <th className="p-3 font-medium">Descripción</th>
                                            <th className="p-3 font-medium">Tipo</th>
                                            <th className="p-3 font-medium">Monto</th>
                                            <th className="p-3 font-medium">Estado</th>
                                            <th className="p-3 font-medium">Comprobante</th>
                                            <th className="p-3 font-medium">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm">
                                        {clientTransactions.map(tx => (
                                            <tr key={tx.TransaccionId} className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3">{new Date(tx.Fecha).toLocaleDateString()}</td>
                                                <td className="p-3">{tx.Descripcion}</td>
                                                <td className="p-3 capitalize">{tx.Tipo}</td>
                                                <td className={`p-3 font-medium ${tx.Tipo === 'credito' ? 'text-emerald-600' : 'text-slate-700'}`}>
                                                    ${tx.Monto}
                                                </td>
                                                <td className="p-3">
                                                    <TransaccionStatus status={tx.Estado} />
                                                </td>
                                                <td className="p-3">
                                                    {tx.NumeroFactura && (
                                                        <a 
                                                            href={`${import.meta.env.VITE_API_BASE_URL || ''}/uploads/${tx.NumeroFactura}`} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 hover:underline flex items-center gap-1"
                                                        >
                                                            <FileText size={14} /> Ver
                                                        </a>
                                                    )}
                                                </td>
                                                <td className="p-3">
                                                    {tx.Estado === 'pendiente' && tx.Tipo === 'credito' && (
                                                        <button 
                                                            onClick={() => handleApproveTransaction(tx.TransaccionId)}
                                                            className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded hover:bg-emerald-200 transition-colors"
                                                        >
                                                            Aprobar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {clientTransactions.length === 0 && (
                                            <tr>
                                                <td colSpan="7" className="p-8 text-center text-slate-400">
                                                    No hay transacciones registradas.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default OrganizationManager;
